import { Tune } from "@mui/icons-material";
import { Box, Button, Divider, Menu, MenuItem } from "@mui/material";
import { MouseEvent, useState } from "react";
import { useTranslation } from "react-i18next";

import { useAppDispatch, useAppSelector } from "~/app/store";
import NavSearchInput from "~/features/navbar/NavSearchInput";

import {
  mixpanelTrack,
  useMixpanelContextName,
  useMixpanelPageName
} from "~/lib/mixpanel-tracking";

import {
  setOrderTypeFilter,
  setPutWallIdentifierFilter,
  setSearch
} from "./ship.slice";

export function ShipSearch() {
  const { t } = useTranslation();
  const dispatch = useAppDispatch();
  const trackedPageName = useMixpanelPageName();
  const trackedShipFiltersContextName = useMixpanelContextName(
    "Order Consolidation Filters Menu"
  );

  const orderTypeFilter = useAppSelector((state) => state.ship.orderTypeFilter);
  const putWallIdentifierFilter = useAppSelector(
    (state) => state.ship.putWallIdentifierFilter
  );
  const search = useAppSelector((state) => state.ship.search);

  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);

  const rackFilters: string[] = ["A", "B", "C", "D"];

  // TODO: this should come from an FC config
  const orderTypeFilters: string[] = ["Shop", "Fresh"];

  const handleOpenFilters = (event: MouseEvent<HTMLElement>) => {
    mixpanelTrack({
      trackedPageName,
      type: "Button Click",
      label: "Open Order Consolidation Filters Menu"
    });
    setAnchorEl(event.currentTarget);
  };

  const handleClose = () => {
    setAnchorEl(null);
  };

  return (
    <>
      <NavSearchInput
        textInput={search || ""}
        setTextInput={(text) => dispatch(setSearch(text))}
        isAutocomplete
        searchPlaceholder={t("search orders")}
        autocompleteSearchCb={(input) => {
          mixpanelTrack({
            trackedPageName,
            type: "Search",
            label: "Search Orders",
            eventProperties: {
              "Search Text": input
            }
          });
          dispatch(setSearch(input));
        }}
        autocompleteOptions={[]}
        hideOptions
        clearOnBlur={false}
      />
      <Button
        aria-label="filters"
        aria-controls="simple-menu"
        aria-haspopup="true"
        onClick={handleOpenFilters}
        style={{
          minWidth: 40,
          //add margin since we removed `NavSearchInput` margins
          marginLeft: "24px"
        }}
      >
        <Tune />
      </Button>
      <Menu
        id="simple-menu"
        anchorEl={anchorEl}
        keepMounted
        open={!!anchorEl}
        onClose={handleClose}
      >
        {rackFilters.map((rack) => (
          <MenuItem
            key={rack}
            onClick={() => {
              mixpanelTrack({
                trackedPageName: trackedShipFiltersContextName,
                type: "Select Option",
                label: "Select Rack Filter",
                eventProperties: {
                  Rack: rack
                }
              });
              dispatch(setPutWallIdentifierFilter(rack));
            }}
            sx={{
              minWidth: 150,
              backgroundColor:
                putWallIdentifierFilter === rack ? "gray.light" : undefined
            }}
          >
            {`Rack ${rack}`}
          </MenuItem>
        ))}
        <Divider style={{ marginTop: 2, marginBottom: 2, height: 3 }} />
        {orderTypeFilters.map((orderType) => (
          <MenuItem
            key={orderType}
            onClick={() => {
              mixpanelTrack({
                trackedPageName: trackedShipFiltersContextName,
                type: "Select Option",
                label: "Select Order Type Filter",
                eventProperties: {
                  "Order Type": orderType
                }
              });
              dispatch(setOrderTypeFilter(orderType));
            }}
            sx={{
              minWidth: 150,
              backgroundColor:
                orderTypeFilter === orderType ? "gray.light" : undefined
            }}
          >
            {`${orderType} Orders`}
          </MenuItem>
        ))}
        <Divider style={{ marginTop: 2, marginBottom: 2, height: 3 }} />
        <MenuItem
          key="reset filters"
          onClick={() => {
            mixpanelTrack({
              trackedPageName: trackedShipFiltersContextName,
              type: "Button Click",
              label: "Reset Order Consolidation Filters"
            });
            dispatch(setOrderTypeFilter(null));
            dispatch(setPutWallIdentifierFilter(null));
            handleClose();
          }}
        >
          <Box display="flex" justifyContent="space-between">
            <Box>{t("reset filters")}</Box>
          </Box>
        </MenuItem>
      </Menu>
    </>
  );
}
